import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Material {
  id: number;
  nome: string;
  codigo: string | null;
  unidade_medida: string | null;
  quantidade_atual: string | number;
  valor_unitario: string | number;
  status: string;
}

const UNIDADES = [
  ['un', 'Unidade'],
  ['pc', 'Peca'],
  ['par', 'Par'],
  ['kit', 'Kit'],
  ['L', 'Litro'],
  ['mL', 'Mililitro'],
  ['kg', 'Quilograma'],
  ['m', 'Metro'],
];

function erroMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function numero(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function moeda(v: unknown): string {
  return numero(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function MateriaisOficina() {
  const [materiais, setMateriais] = useState<Material[]>([]);
  const [form, setForm] = useState<Partial<Material>>({ status: 'ativo', unidade_medida: 'un', quantidade_atual: 0, valor_unitario: 0 });
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [salvando, setSalvando] = useState(false);
  const editando = Boolean(form.id);

  async function carregar() {
    setErro('');
    setMateriais(await api<Material[]>('/estoque/materiais.php'));
  }

  useEffect(() => {
    carregar().catch((e) => setErro(erroMsg(e)));
  }, []);

  function limpar() {
    setForm({ status: 'ativo', unidade_medida: 'un', quantidade_atual: 0, valor_unitario: 0 });
  }

  async function salvar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setSalvando(true);
    const body = {
      id: form.id,
      nome: (form.nome || '').trim(),
      codigo: form.codigo || null,
      unidade_medida: form.unidade_medida || 'un',
      quantidade_atual: numero(form.quantidade_atual),
      valor_unitario: numero(form.valor_unitario),
      status: form.status || 'ativo',
    };

    try {
      if (form.id) await api('/estoque/materiais.php', { method: 'PUT', body });
      else await api('/estoque/materiais.php', { method: 'POST', body });
      setMsg(editando ? 'Material atualizado.' : 'Material cadastrado.');
      limpar();
      await carregar();
    } catch (e2) {
      setErro(erroMsg(e2));
    } finally {
      setSalvando(false);
    }
  }

  // Material nunca e excluido, so desativado (o endpoint DELETE desativa).
  async function excluir(m: Material) {
    setErro('');
    setMsg('');
    try {
      await api('/estoque/materiais.php?id=' + m.id, { method: 'DELETE' });
      setMsg('Material desativado.');
      await carregar();
    } catch (e) {
      setErro(erroMsg(e));
    }
  }

  return (
    <div>
      <div className="titulo-pg">
        <h1>Materiais</h1>
        <button className="btn sec sm" onClick={() => carregar().catch((e) => setErro(erroMsg(e)))}>Atualizar</button>
      </div>

      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      <div className="grid cols2">
        <form className="card" onSubmit={salvar}>
          <h3>{editando ? 'Editar material' : 'Novo material'}</h3>
          <label>Nome *</label>
          <input className="input" value={form.nome || ''} onChange={(e) => setForm({ ...form, nome: e.target.value })} required autoFocus />

          <div className="grid cols2">
            <div>
              <label>Codigo</label>
              <input className="input" value={form.codigo || ''} onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
            </div>
            <div>
              <label>Unidade</label>
              <select value={form.unidade_medida || 'un'} onChange={(e) => setForm({ ...form, unidade_medida: e.target.value })}>
                {UNIDADES.map(([value, label]) => <option key={value} value={value}>{label} ({value})</option>)}
              </select>
            </div>
          </div>

          <div className="grid cols2">
            <div>
              <label>{editando ? 'Estoque atual' : 'Quantidade inicial'}</label>
              <input className="input" type="number" min="0" step="0.001" value={form.quantidade_atual ?? ''} onChange={(e) => setForm({ ...form, quantidade_atual: e.target.value })} disabled={editando} />
            </div>
            <div>
              <label>Valor unitario</label>
              <input className="input" type="number" min="0" step="0.01" value={form.valor_unitario ?? ''} onChange={(e) => setForm({ ...form, valor_unitario: e.target.value })} />
            </div>
          </div>

          <label>Status</label>
          <select value={form.status || 'ativo'} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
          </select>

          <div className="form-actions">
            {editando && <button type="button" className="btn sec" onClick={limpar}>Cancelar</button>}
            <button className="btn" disabled={salvando}>{salvando ? 'Salvando...' : (editando ? 'Salvar material' : 'Cadastrar material')}</button>
          </div>
        </form>

        <div className="tabela-wrap">
          <table>
            <thead><tr><th>Material</th><th>Estoque</th><th>Valor</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {materiais.map((m) => (
                <tr key={m.id}>
                  <td><strong>{m.nome}</strong>{m.codigo ? <><br /><small>{m.codigo}</small></> : null}</td>
                  <td>{numero(m.quantidade_atual).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} {m.unidade_medida || ''}</td>
                  <td>{moeda(m.valor_unitario)}</td>
                  <td><span className="badge">{m.status}</span></td>
                  <td className="acoes">
                    <button className="btn sec sm" onClick={() => setForm({ ...m, quantidade_atual: numero(m.quantidade_atual), valor_unitario: numero(m.valor_unitario) })}>Editar</button>
                    <button className="btn danger sm" onClick={() => excluir(m)}>Desativar</button>
                  </td>
                </tr>
              ))}
              {!materiais.length && <tr><td colSpan={5} className="vazio">Nenhum material cadastrado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
